import { AsyncPipe, JsonPipe, NgClass, NgStyle, NgTemplateOutlet, SlicePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  input,
  Input,
  output,
  Output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ArkhineoScheduledTask } from '@compiere-ws/models/apiz-domains';
import { DocserverDisplayedTag } from '@compiere-ws/models/docserverDisplayedTag';
import { TagItem } from '@compiere-ws/models/tag-json';
import { DocServerService } from '@compiere-ws/services/doc-server/doc-server.service';
import { UploadedFile } from '@iupics-components/models/uploaded-file';
import { FilterListOperator, FilterListOptions } from '@iupics-components/pipes/filter-list/filter-list.pipe';
import ButtonUiComponent from '@iupics-components/standard/fields/button-ui/button-ui.component';
import InputTextUiComponent from '@iupics-components/standard/fields/input-text-ui/input-text-ui.component';
import { EditViewUtils } from '@iupics-components/standard/layouts/edit-view-ui/utils/edit-view.utils';
import PreviewDocComponent from '@iupics-components/standard/preview-doc/preview-doc.component';
import { TagsEditorUiComponent } from '@iupics-components/standard/tags-editor-ui/tags-editor-ui.component';
import { MessageManagerService } from '@iupics-manager/managers/message/message-manager.service';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { OverridedCSS } from '@iupics-manager/models/overrided-css';
import { ClickDoubleDirective } from '@iupics-util/directives/click-double.directive';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { MessageService, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FileUpload } from 'primeng/fileupload';
import { MessagesModule } from 'primeng/messages';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DocServerFormatValuePipe } from '../../../iupics-util/pipes/doc-server-format-value/doc-server-format-value.pipe';
import { DocServerUrlPipe } from '../../../iupics-util/pipes/doc-server-url/doc-server-url.pipe';
import { FilterListPipe } from '../../pipes/filter-list/filter-list.pipe';
import PrimeOverlayComponent from '../prime-overlay/prime-overlay.component';
import { InViewportDirective } from './in-viewport.directive';
import { PrimeFileUploadLogoFilePipe } from './prime-fileupload-logo-file.pipe';

type TagColumns = {
  files: UploadedFile[];
  length: number;
  tags: DocserverDisplayedTag[];
  visible: DocserverDisplayedTag[];
  overflow: DocserverDisplayedTag[];
};
type ThumbState = { src?: any; failed?: boolean };
@Component({
  selector: 'iu-prime-fileupload',
  templateUrl: './prime-fileupload.component.html',
  styleUrls: ['./prime-fileupload.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ProgressSpinnerModule,
    NgClass,
    NgTemplateOutlet,
    ButtonModule,
    PrimeOverlayComponent,
    SharedModule,
    TableModule,
    NgStyle,
    MessagesModule,
    PreviewDocComponent,
    InputTextUiComponent,
    forwardRef(() => ButtonUiComponent),
    AsyncPipe,
    SlicePipe,
    TranslateModule,
    DocServerUrlPipe,
    DocServerFormatValuePipe,
    FilterListPipe,
    TagsEditorUiComponent,
    PrimeFileUploadLogoFilePipe,
    JsonPipe,
    ClickDoubleDirective,
    TooltipModule,
    InViewportDirective,
  ],
})
export default class PrimeFileuploadComponent extends FileUpload {
  @ViewChild(PreviewDocComponent)
  private previewDocComponent: PreviewDocComponent;
  @ViewChild('advancedfileinput') advancedfileinput: ElementRef;
  @Output()
  uploadFiles: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  downloadEmitter = new EventEmitter<any>();
  @Output()
  deleteEmitter = new EventEmitter<any>();
  @Output()
  fileSelectEmitter = new EventEmitter<any>();
  @Output()
  openFileOnSideEmitter = new EventEmitter<any>();
  attachUrl = output<{ url: string; tags: TagItem[] }>();

  readonly inputId = input<string | number>();

  @Input() displayedTags: DocserverDisplayedTag[] = [];
  @Input() uploadedFiles: UploadedFile[] = [];
  @Input() linkedFiles: UploadedFile[] = [];
  @Input() uploadingFiles = [] as any;
  @Input() displayButtons = true;
  @Input() displayFileUploaded = true;
  @Input() isLoaderActive = false;
  @Input() isReadOnly = false;
  @Input() canUpload = true;
  @Input() canDelete = true;
  @Input() canDownload = true;
  @Input() canPreview = false;
  @Input() supportLinks = false;
  @Input() linkedFileList_title = 'fileupload.linked_files_list';
  @Input() uploadedFileList_noLinkedFiles = 'fileupload.no_linked_files';
  @Input() uploadedFileList_title = 'fileupload.files_list';
  @Input() uploadedFileList_noFiles = 'fileupload.no_files';
  @Input() fileList_createdDate = 'fileupload.createdDate';
  @Input() uploadingFileList_title = 'fileupload.add_files';
  @Input() uploadingFileList_drag = 'fileupload.label';
  @Input() uploadBtn_confirm = 'fileupload.upload_files';
  @Input() uploadedFileList_search = 'fileupload.inputSearch';
  @Input() fromDocServer = true;
  @Input() uploadBtn_cancel = 'fileupload.clear';
  @Input() overridedCSS: OverridedCSS;

  selectedFile: UploadedFile;
  selectedOverflowTags: DocserverDisplayedTag[] = [];
  filterListOptions: FilterListOptions;
  filterLinkedListOptions: FilterListOptions;
  tags: TagItem[][] = [];

  private readonly docServerService = inject(DocServerService);
  private readonly messageService = inject(MessageService);
  private readonly translator = inject(TranslateService);
  // used outside the template to fetch thumbnails lazily
  readonly #docServerUrlPipe = new DocServerUrlPipe();
  readonly #tagColumnsCache = new Map<string, TagColumns>();
  #tagLabelsSource: DocserverDisplayedTag[];
  readonly #tagLabels = new Map<string, string>();
  protected readonly thumbs = signal<Record<string, ThumbState>>({});

  protected readonly arkhineoBaseUrl = environment.constant['arkhineoBaseUrl'];
  protected readonly arkhineoCfeId = environment.constant['arkhineoCfeId'];
  protected readonly arkhineoSectionId = environment.constant['arkhineoSectionId'];

  addFile(event: Event) {
    event.stopPropagation();
    // simule un click sur le input file.
    this.advancedfileinput?.nativeElement.click();
  }

  remove(event: Event, i: number) {
    // retirer un fichier de la liste des fichiers à uploadé.
    event.stopPropagation();
    this.tags.splice(i, 1);
    super.remove(event, i);
  }

  clear() {
    // keep tags aligned with the pending files
    this.tags = [];
    super.clear();
  }

  /**
   * Demande au parent l'upload des fichiers de la liste
   */
  onClickUploadFiles() {
    this.uploadFiles.emit({ files: this.files, tags: this.tags });
  }

  /**
   * Appellé par le parent (input-file-ui) pour retirer un fichier de la liste
   * @param {File} file
   */
  removeFile(file: File) {
    const index = this.files.indexOf(file);
    this.files.splice(index, 1);
    this.tags.splice(index, 1);
  }

  /**
   * lance l'emitter pour le téléchargement du fichier
   * @param {Event} event
   * @param {string} docId
   * @param {index} index
   */
  downloadFile(event: Event, file: UploadedFile, index: number) {
    event.stopPropagation();
    this.downloadEmitter.emit({ file: file });
  }
  /**
   * Affiche les autres tags disponibles pour un fichier
   * @param {Event} event
   * @param {string} docId
   * @param {index} index
   */
  showMoreTags(event: Event, file: UploadedFile) {
    event.stopPropagation();
    this.downloadEmitter.emit({ file: file });
  }
  /**
   * lance la copie du lien du fichier
   * @param {Event} event
   * @param {string} docId
   * @param {index} index
   */
  copyLinkFile(event: Event, file: UploadedFile, index: number) {
    event.stopPropagation();
    const urlPreview =
      this.fromDocServer && file.provider !== 'external'
        ? this.docServerService.getUrlPreview(file.src as string)
        : file.src;
    EditViewUtils.copyToClipboard(urlPreview as string);
    this.messageService.add({
      key: 'quick-toast',
      severity: 'success',
      summary: this.translator.instant('generic.success'),
      detail: this.translator.instant('joinFiles.tooltip_copied'),
      life: 1500,
      closable: false,
    });
  }

  /**
   * Lance l'emitter pour la suppression du fichier
   * @param {Event} event
   * @param {string} docId
   * @param {index} index
   */
  deleteFile(event: Event, file: UploadedFile, index: number) {
    event.stopPropagation();
    this.deleteEmitter.emit({ file, index });
  }

  onFileSelect(event) {
    const files: FileList = event.dataTransfer ? event.dataTransfer.files : event.target.files;
    for (let i = 0; i < files.length; i++) {
      this.tags.push([]);
    }
    super.onFileSelect(event);
    this.fileSelectEmitter.emit(event);
  }

  onPreview(event, file: any, index: number) {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    if (this.docServerService.hasPreview(file)) {
      const fileName = file.extension
        ? (file.name as string).endsWith(`.${file.extension.replace(/^\./, '')}`)
          ? file.name
          : `${file.name}.${file.extension}`
        : file.name;
      this.previewDocComponent.onPreview(
        event,
        fileName,
        file.src || this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file))
      );
    }
  }
  updateFilterOptions(event: any, inputValue: string, src: string) {
    const taggedProperties = ['name'];
    for (const tag of this.displayedTags) {
      taggedProperties.push('tags.META|' + tag.columnName.toUpperCase());
      taggedProperties.push('tags.META|' + tag.columnName.toUpperCase() + '$');
    }
    if (src === 'files') {
      this.filterListOptions = {
        objProperties: taggedProperties,
        operator: FilterListOperator.CONTAINS,
        value: inputValue,
      };
    }
    if (src == 'linkedFiles') {
      this.filterLinkedListOptions = {
        objProperties: taggedProperties,
        operator: FilterListOperator.CONTAINS,
        value: inputValue,
      };
    }
  }

  openFileOnSide(event: Event, file, index: number, files?: any[]) {
    event.stopPropagation();
    if (this.docServerService.hasPreview(file)) {
      // sibling previewable files let the side preview offer previous / next
      const siblings = (files ?? []).filter((f) => f?.provider !== 'external' && f?.src && this.docServerService.hasPreview(f));
      const index = siblings.indexOf(file);
      const hasList = siblings.length > 1 && index >= 0;
      this.openFileOnSideEmitter.emit({
        ...this.toSidePreview(file),
        files: hasList ? siblings.map((f) => this.toSidePreview(f)) : undefined,
        index: hasList ? index : undefined,
      });
    }
  }

  private toSidePreview(file): { fileName: string; urlFile: any } {
    const fileName = file.extension
      ? (file.name as string).endsWith(`.${file.extension.replace(/^\./, '')}`)
        ? file.name
        : `${file.name}.${file.extension}`
      : file.name;
    return {
      fileName: fileName,
      urlFile: file.src || this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file)),
    };
  }

  attachAnUrl(event: Event, urlInput: InputTextUiComponent, tagsInput: TagsEditorUiComponent) {
    const url = urlInput.fieldValue;
    if (!url) {
      // nothing to attach: keep the selected tags
      event?.stopPropagation();
      return;
    }
    const tags = tagsInput.autoComplete.selected();
    urlInput.fieldValue = '';
    tagsInput.autoComplete.selected.set([]);
    event.stopPropagation();
    this.attachUrl.emit({ url, tags });
  }

  openLink(event: Event, url: string) {
    event?.stopPropagation();
    window.open(url, '_blank');
  }

  showTaskMessage(task: ArkhineoScheduledTask) {
    const message =
      task.retryCount === 0
        ? this.translator.instant('fileupload.arkhineo.pending')
        : this.translator.instant('fileupload.arkhineo.retry', {
            retryCount: task.retryCount,
            date: DateUtils.simpleFormatStr(task.nextAttemptAt, DateUtils.humanReadableDateTimeFormat),
          });
    MessageManagerService.newMessageStatic(
      new IupicsMessage(this.translator.instant('generic.info'), message, 'warning')
    );
  }

  getTagValue(file: UploadedFile, tag: DocserverDisplayedTag): any {
    const tags = file?.tags as any;
    if (!tags || !tag?.columnName) {
      return undefined;
    }
    const key = 'META|' + tag.columnName.toUpperCase();
    return tags[key + '$'] ? tags[key + '$'] : tags[key];
  }

  hasTagValue(file: UploadedFile, tag: DocserverDisplayedTag): boolean {
    const value = this.getTagValue(file, tag);
    return value !== undefined && value !== null && value !== '';
  }

  /**
   * Metadata columns that have a value for at least one file of the list (max 4 shown, rest in overlay)
   */
  getTagColumns(files: UploadedFile[], listKey: string): TagColumns {
    const list = files ?? [];
    const cached = this.#tagColumnsCache.get(listKey);
    if (cached && cached.files === list && cached.length === list.length && cached.tags === this.displayedTags) {
      return cached;
    }
    const nonEmpty = (this.displayedTags ?? []).filter((tag) => list.some((file) => this.hasTagValue(file, tag)));
    const columns: TagColumns = {
      files: list,
      length: list.length,
      tags: this.displayedTags,
      visible: nonEmpty.slice(0, 4),
      overflow: nonEmpty.slice(4),
    };
    this.#tagColumnsCache.set(listKey, columns);
    return columns;
  }

  showMoreTagsOverlay(event: Event, file: UploadedFile, overflow: DocserverDisplayedTag[], overlay: PrimeOverlayComponent) {
    this.selectedFile = file;
    this.selectedOverflowTags = overflow;
    overlay.toggle(event);
  }

  /**
   * Header label of a metadata column: i18n key fileupload.meta.<COLUMN>, else backend label, else readable key
   */
  getTagLabel(tag: DocserverDisplayedTag): string {
    if (this.#tagLabelsSource !== this.displayedTags) {
      this.#tagLabelsSource = this.displayedTags;
      this.#tagLabels.clear();
    }
    const columnName = (tag?.columnName ?? '').toUpperCase();
    let label = this.#tagLabels.get(columnName);
    if (label === undefined) {
      const key = 'fileupload.meta.' + columnName;
      const translated = columnName ? this.translator.instant(key) : key;
      if (translated && translated !== key) {
        label = translated;
      } else if (tag?.colLabel && /[a-z]/.test(tag.colLabel)) {
        label = tag.colLabel;
      } else {
        label = this.humanizeKey(tag?.colLabel || tag?.columnName || '');
      }
      this.#tagLabels.set(columnName, label);
    }
    return label;
  }

  isStatusTag(tag: DocserverDisplayedTag): boolean {
    return /status|statut/i.test(tag?.columnName ?? '') || /status|statut/i.test(tag?.colLabel ?? '');
  }

  /** OTHER_GED -> Other GED, E-INVOICING -> E-Invoicing */
  private humanizeKey(key: string): string {
    return key
      .split(/[_\s]+/)
      .filter((word) => word.length > 0)
      .map((word) =>
        word
          .split('-')
          .map((part) => (part.length <= 3 ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()))
          .join('-')
      )
      .join(' ');
  }

  /** Fetch a server thumbnail once, when its cell becomes visible */
  loadThumb(url: string) {
    if (!url || typeof url !== 'string' || this.thumbs()[url]) {
      return;
    }
    this.setThumb(url, {});
    this.#docServerUrlPipe.transform(url).then(
      (src) => this.setThumb(url, src ? { src } : { failed: true }),
      () => this.setThumb(url, { failed: true })
    );
  }

  onThumbError(url: string) {
    if (url && typeof url === 'string') {
      this.setThumb(url, { failed: true });
    }
  }

  private setThumb(url: string, state: ThumbState) {
    this.thumbs.update((thumbs) => ({ ...thumbs, [url]: state }));
  }
}
